<script setup lang="ts">
  import { computed } from 'vue';
  import { MIME_TYPE } from './lib';
  import { PictureFit, PictureFormat } from './Picture.types';

  const props = defineProps({
    path: {
      type: String,
      default: '',
      required: true,
      validator: (value: string) => {
        const extension = value.match(/\.(\w+)$/)?.[1] as PictureFormat;
        return extension ? Object.values(PictureFormat).includes(extension) : false;
      },
    },
    host: {
      type: String,
      default: 'https://assets.travelatacdn.ru/',
    },
    extensions: {
      type: Array as () => PictureFormat[],
      default: () => [PictureFormat.WEBP],
    },
    fit: {
      type: String,
      default: PictureFit.Cover,
    },
    position: {
      type: String,
      default: '',
    },
    lazy: {
      type: Boolean,
      default: true,
    },
    highResolution: {
      type: Boolean,
      default: true,
    },
  });

  const EXTENSION_REGEX = /\.(\w+)$/;
  const NAME_REGEX = /\/([^/]+?)(\.\w+)?$/;
  const HTTP_REGEX = /^(https?:\/\/|\/\/)/;

  const computedStyles = computed(() => ({
    objectFit: props.fit,
    ...(props.position ? { objectPosition: props.position } : {}),
  }));

  const isAbsoluteUrl = computed(() => HTTP_REGEX.test(props.path));
  const url = computed(() => (isAbsoluteUrl.value ? props.path : props.host + props.path));
  const name = computed(() => props.path.match(NAME_REGEX)?.[1] ?? '');
  const baseUrl = computed(() => url.value.replace(EXTENSION_REGEX, ''));
  const baseExtension = computed(() => url.value.match(EXTENSION_REGEX)?.[1] as PictureFormat);
  const isSvg = computed(() => baseExtension.value === PictureFormat.SVG);
  const loading = computed(() => (props.lazy ? 'lazy' : 'eager'));

  const extensionList = computed(() => [...new Set([baseExtension.value, ...props.extensions])]);

  const getImages = (extension: PictureFormat) => {
    const src = extension === baseExtension.value ? 'src' : extension;
    const srcHighRes = extension === baseExtension.value ? 'srcHighRes' : `${extension}HighRes`;

    const sources = {
      [src]: `${baseUrl.value}.${extension}`,
    };

    if (props.highResolution) {
      sources[srcHighRes] = `${baseUrl.value}@2x.${extension}`;
    }

    return sources;
  };

  const images = computed(() => {
    if (isSvg.value) {
      return { src: url.value };
    }

    return extensionList.value.reduce(
      (acc, ext) => ({
        ...acc,
        ...getImages(ext),
      }),
      {} as Record<string, string>,
    );
  });

  const sources = computed(() => {
    return isSvg.value
      ? {}
      : extensionList.value
          .filter((ext) => ext !== baseExtension.value)
          .reduce((acc, ext) => {
            const srcHighRes = `${ext}HighRes`;

            return {
              ...acc,
              [ext]: props.highResolution
                ? `${images.value[srcHighRes]} 2x, ${images.value[ext]} 1x`
                : `${images.value[ext]} 1x`,
            };
          }, {} as Record<string, string>);
  });

  const srcset = computed(() => {
    if (isSvg.value) {
      return '';
    }

    return props.highResolution ? `${images.value.srcHighRes} 2x, ${images.value.src} 1x` : `${images.value.src} 1x`;
  });
</script>

<template>
  <picture :class="['tl-picture', { 'tl-picture_svg': isSvg }]">
    <template v-if="isSvg">
      <img
        :src="images.src"
        :alt="name"
        :loading="loading"
        data-test-id="img"
        :style="computedStyles"
        class="tl-picture__img"
        v-on="$listeners"
      />
    </template>

    <template v-else>
      <template v-if="Object.keys(sources).length > 0">
        <source
          v-for="(src, extension) in sources"
          :key="extension"
          :srcset="src"
          :type="MIME_TYPE[extension]"
          data-test-id="source"
        />
      </template>

      <img
        :srcset="srcset"
        :src="images.src"
        :alt="name"
        :loading="loading"
        data-test-id="img"
        :style="computedStyles"
        class="tl-picture__img"
        v-on="$listeners"
      />
    </template>
  </picture>
</template>

<style lang="scss" scoped>
  .tl-picture {
    $root: &;

    display: block;
    height: 100%;
    overflow: hidden;
    position: relative;
    width: 100%;

    &_svg {
      #{$root}__img {
        position: static;
      }
    }

    &__img {
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }
  }
</style>
